import { FC, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { GridColDef } from '@mui/x-data-grid-premium';
import styled from 'styled-components';

import { DataGridPremium, IconButton } from '@clatter/ui';
import { useDeepCompareMemo } from '@clatter/platform';
import { displayNameColumn, createdAtColumn, updatedAtColumn } from '../../../../helpers/dataGrid';
import { addNotification } from '../../../../store';
import {
  useGetMicrositeTemplateBlocksQuery,
  useDeleteMicrositeTemplateBlockMutation,
  Block,
} from '../../../../services';
import { NgBreadcrumbs, NgButton, NgCard, NgCardContent, NgCardHeader } from '../../../UI';
import { BlockCreateEditModal } from './components/BlockCreateEditModal';

export interface BlocksPageProps {
  workspaceData: any;
  isLoading?: boolean;
}

const StyledHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const BlocksPage: FC<BlocksPageProps> = ({ isLoading: isParentLoading }) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<Block | null>(null);

  const { data: blocks = [], isLoading: isBlocksLoading } = useGetMicrositeTemplateBlocksQuery();
  const [deleteBlock, { isLoading: isDeleting }] = useDeleteMicrositeTemplateBlockMutation();

  const isLoading = isParentLoading || isBlocksLoading;

  const rows = useDeepCompareMemo(
    () =>
      (blocks || [])
        .filter(
          (block) => block?.type?.trim().toLowerCase() == 'block' || block?.type?.trim().toLowerCase() == 'layout',
        )
        .map((block) => ({
          id: block?.ID,
          ...block,
        })),
    [blocks],
  );

  const handleCreateClick = () => {
    setEditingBlock(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (block: Block) => {
    setEditingBlock(block);
    setIsModalOpen(true);
  };

  const handleDeleteClick = async (block: Block) => {
    if (window.confirm(`Delete "${block?.displayName || block?.name}" block?`)) {
      try {
        await deleteBlock({ blockId: block.id }).unwrap();
        dispatch(
          addNotification({
            type: 'success',
            message: 'Block deleted successfully',
          }),
        );
      } catch (error) {
        dispatch(
          addNotification({
            type: 'error',
            message: 'Failed to delete block',
          }),
        );
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingBlock(null);
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
      { ...displayNameColumn, field: 'displayName', headerName: 'Display Name' },
      { field: 'name', headerName: 'Name', flex: 0.3 },
      { ...createdAtColumn, field: 'createdAt', headerName: 'Created' },
      { ...updatedAtColumn, field: 'updatedAt', headerName: 'Updated' },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 130,
        sortable: false,
        disableExport: true,
        renderCell: (params) => (
          <>
            {/*@ts-ignore*/}
            <IconButton onClick={() => handleEditClick(params.row)} tooltip="Edit">
              <EditOutlinedIcon />
            </IconButton>

            {/*@ts-ignore*/}
            <IconButton onClick={() => handleDeleteClick(params.row)} tooltip="Delete" disabled={isDeleting}>
              <DeleteOutlinedIcon />
            </IconButton>
          </>
        ),
      },
    ];
  }, [isDeleting]);

  return (
    <NgCard>
      <NgCardContent>
        <NgCardHeader hideFilters>
          <NgBreadcrumbs
            data={[{ id: 'blocks', name: 'Blocks' }]}
            onClick={() => {
              //
            }}
          />
        </NgCardHeader>
        <StyledContainer>
          <StyledHeader>
            <NgButton onClick={handleCreateClick}>Create Block</NgButton>
          </StyledHeader>

          <DataGridPremium
            name="blocks-list"
            rows={rows}
            loading={isLoading}
            columns={columns}
            getRowId={(row: any) => row.id}
            onRowClick={() => {}}
            selectable={false}
            apiRef={null}
            renderCustomToolbar={null}
            onRowsPerSizeChange={() => {}}
            defaultSortField={null}
            disableColumnFilter={false}
            rowsPerPageOptions={[25, 50, 100]}
            selectedRowsPerPage={25}
            multilineItemsLimit={null}
            dataGridProps={{
              onRowDoubleClick: (params: any) => {
                handleEditClick(params.row);
              },
            }}
            disablePersist
          />
        </StyledContainer>

        {isModalOpen && <BlockCreateEditModal blocks={blocks} block={editingBlock} onClose={handleModalClose} />}
      </NgCardContent>
    </NgCard>
  );
};
